import { Form, Link } from "@inertiajs/react"
import { Check } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { useOnline } from "@/hooks/use-online"
import {
  messagesPath,
  reactivateCreatorPausedPath,
  reactivationRequestCreatorPausedPath,
} from "@/routes"

export interface PausedCtaProps {
  brandName: string
  /** `"member_self" | "brand_admin" | null` — drives which CTA renders. */
  pauseSource: string | null
  /** Brand-paused only: a reactivation request is already on file. */
  reactivationRequested: boolean
}

/**
 * OKS-561 — Source-dependent CTA zone for the paused member view
 * (UX Spec §2.4). Never a dead-end (PRD §3):
 *
 *   - **Member-self paused** → a single full-width "Reactivate my spot"
 *     (primary). Submits `POST /paused/reactivate`; the server flips the
 *     membership back to active and redirects to `/missions` with a
 *     celebratory toast (success 🎉).
 *   - **Brand paused** → "Message {brand}" (primary → the creator↔brand
 *     message thread) PLUS either:
 *       - "Request reactivation" (secondary, `POST /paused/reactivation_request`)
 *         when no request is on file, or
 *       - a static "{brand} notified · Request sent ✓" chip once requested
 *         (the reactivation-requested interim state — idempotent, no
 *         re-submit).
 *
 * The two POSTs use Inertia `<Form>` (Crew rule — no react-hook-form/zod),
 * disabling the submit while `processing` (no double-submit) and while
 * offline (`useOnline`). A `null`/unknown source falls back to the
 * message-thread contact so the member always has a way to reach the brand.
 */
export function PausedCta({
  brandName,
  pauseSource,
  reactivationRequested,
}: PausedCtaProps) {
  const { t } = useTranslation()
  const online = useOnline()

  if (pauseSource === "member_self") {
    return (
      <Form
        action={reactivateCreatorPausedPath()}
        method="post"
        options={{ preserveScroll: true }}
        data-testid="paused-reactivate-form"
      >
        {({ processing }) => (
          <Button
            type="submit"
            className="w-full"
            disabled={processing || !online}
            data-testid="paused-cta-reactivate"
          >
            {t("crew_paused.cta.reactivate")}
          </Button>
        )}
      </Form>
    )
  }

  // Brand-paused (and the defensive null-source fallback): always offer the
  // message thread; add the request path unless one is already on file.
  return (
    <div className="flex flex-col gap-3">
      <Button asChild className="w-full" data-testid="paused-cta-message">
        <Link href={messagesPath()}>
          {t("crew_paused.cta.message", { brand: brandName })}
        </Link>
      </Button>

      {reactivationRequested ? (
        <p
          className="text-muted-foreground flex items-center justify-center gap-2 text-sm"
          role="status"
          data-testid="paused-request-sent-chip"
        >
          <Check className="size-4 shrink-0" aria-hidden="true" />
          {t("crew_paused.cta.request_sent", { brand: brandName })}
        </p>
      ) : pauseSource === "brand_admin" ? (
        <Form
          action={reactivationRequestCreatorPausedPath()}
          method="post"
          options={{ preserveScroll: true }}
          data-testid="paused-request-form"
        >
          {({ processing }) => (
            <Button
              type="submit"
              variant="outline"
              className="w-full"
              disabled={processing || !online}
              data-testid="paused-cta-request"
            >
              {t("crew_paused.cta.request")}
            </Button>
          )}
        </Form>
      ) : null}
    </div>
  )
}
